import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Paper, Stack, TextField, Button, Typography, MenuItem, Alert } from '@mui/material';
import { api } from '../api/client';
import type { DriverDto, PageResponse, VehicleDto } from '../api/types';
import { useAuthStore, hasAnyRole } from '../auth/store';

interface FormState {
  plate: string;
  driverId: number | '';
  make: string;
  model: string;
  year: number | '';
  color: string;
  vehicleType: string;
  vin: string;
  notes: string;
  status: string;
}

const initialState: FormState = {
  plate: '', driverId: '', make: '', model: '', year: '',
  color: '', vehicleType: '', vin: '', notes: '', status: 'ACTIVE',
};

export function VehicleFormPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const isNew = !id || id === 'new';
  const { user } = useAuthStore();
  const canEdit = hasAnyRole(user, 'ADMIN', 'OPERADOR');
  const [form, setForm] = useState<FormState>(initialState);
  const [err, setErr] = useState<string | null>(null);

  const drivers = useQuery({
    queryKey: ['drivers-all'],
    queryFn: () => api.get<PageResponse<DriverDto>>('/drivers', { params: { size: 500 } }).then((r) => r.data.content),
  });

  const existing = useQuery({
    queryKey: ['vehicle', id],
    queryFn: () => api.get<VehicleDto>(`/vehicles/${id}`).then((r) => r.data),
    enabled: !isNew,
  });

  useEffect(() => {
    if (existing.data) {
      setForm({
        plate: existing.data.plateDisplay,
        driverId: existing.data.driver?.id ?? '',
        make: existing.data.make ?? '',
        model: existing.data.model ?? '',
        year: existing.data.year ?? '',
        color: existing.data.color ?? '',
        vehicleType: existing.data.vehicleType ?? '',
        vin: existing.data.vin ?? '',
        notes: existing.data.notes ?? '',
        status: existing.data.status,
      });
    }
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        plate: form.plate,
        driverId: form.driverId === '' ? undefined : form.driverId,
        make: form.make || undefined,
        model: form.model || undefined,
        year: form.year === '' ? undefined : Number(form.year),
        color: form.color || undefined,
        vehicleType: form.vehicleType || undefined,
        vin: form.vin || undefined,
        notes: form.notes || undefined,
        status: form.status,
      };
      return isNew ? api.post('/vehicles', body) : api.put(`/vehicles/${id}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      nav('/vehicles');
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } } };
      setErr(ax.response?.data?.message ?? 'Error al guardar');
    },
  });

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>{isNew ? 'Nueva camioneta' : 'Editar camioneta'}</Typography>
      <Paper sx={{ p: 3, maxWidth: 720 }}>
        <Stack spacing={2}>
          {err && <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>}
          <TextField label="Placa" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} disabled={!canEdit} required />
          <TextField select label="Chofer" value={form.driverId} onChange={(e) => setForm({ ...form, driverId: e.target.value === '' ? '' : Number(e.target.value) })} disabled={!canEdit}>
            <MenuItem value=""><em>(sin asignar)</em></MenuItem>
            {(drivers.data ?? []).map((d) => (<MenuItem key={d.id} value={d.id}>{d.fullName}</MenuItem>))}
          </TextField>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Marca" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} disabled={!canEdit} fullWidth />
            <TextField label="Modelo" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} disabled={!canEdit} fullWidth />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Año" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value === '' ? '' : Number(e.target.value) })} disabled={!canEdit} fullWidth />
            <TextField label="Color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} disabled={!canEdit} fullWidth />
            <TextField select label="Tipo" value={form.vehicleType} onChange={(e) => setForm({ ...form, vehicleType: e.target.value })} disabled={!canEdit} fullWidth>
              <MenuItem value=""><em>(ninguno)</em></MenuItem>
              <MenuItem value="CAMIONETA">Camioneta</MenuItem>
              <MenuItem value="PANEL">Panel</MenuItem>
              <MenuItem value="CAJA_SECA">Caja seca</MenuItem>
              <MenuItem value="OTRO">Otro</MenuItem>
            </TextField>
          </Stack>
          <TextField label="VIN" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} disabled={!canEdit} />
          <TextField select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} disabled={!canEdit}>
            <MenuItem value="ACTIVE">Activa</MenuItem>
            <MenuItem value="INACTIVE">Inactiva</MenuItem>
            <MenuItem value="MAINTENANCE">En mantenimiento</MenuItem>
          </TextField>
          <TextField label="Notas" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!canEdit} multiline rows={3} />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => nav('/vehicles')}>Cancelar</Button>
            {canEdit && <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>Guardar</Button>}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
